import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button, Spinner, Offcanvas } from 'react-bootstrap';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import TransactionHistory from './extra/dashboard/TransactionHistory';
import Footer from './Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUserCircle, faUsers } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import Swal from 'sweetalert2';
import PreLoader from './extra/PreLoader';

const Transfer = () => {
    const { http, user } = AuthenticateService();
    const { token, logout } = AuthenticateService();

    // State for beneficiaries
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] = useState(false);
    const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
    const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

    // Existing state
    const [accountNumber, setAccountNumber] = useState('');
    const [accountName, setAccountName] = useState('');
    const [accountNameVerified, setAccountNameVerified] = useState(false);
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [banks, setBanks] = useState([]);
    const [transactionPin, setTransactionPin] = useState('');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isBuying, setIsBuying] = useState(false);
    const [isInitialising, setIsInitialising] = useState(false);
    const [transactionRefrence, settransactionRefrence] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const defaultAmount = [500, 1000, 2000, 5000, 10000, 20000];
    const [selectedBank, setSelectedBank] = useState(null);

    // API configuration with Authorization header
    const apiConfig = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };

    // Custom API function with token
    const apiCall = async (method, url, data = null) => {
        const config = {
            ...apiConfig,
            method: method
        };

        if (data) {
            config.data = data;
        }

        try {
            const response = await http({
                url,
                ...config
            });
            return response;
        } catch (error) {
            if (error.response?.status === 401) {
                logout();
            }
            throw error;
        }
    };

    // Fetch user accounts with token
    const getUserAccounts = async () => {
        try {
            setIsLoadingAccounts(true);
            const response = await http.get('/users/get-details', apiConfig);

            if (response.data.status === true || response.data.status === 1) {
                const accountsData = response.data.data?.accounts || response.data.message?.data?.accounts || [];
                setAccounts(accountsData);

                if (accountsData.length > 0) {
                    setSelectedAccountId(accountsData[0].id);
                    fetchBeneficiaries(accountsData[0].id);
                }
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            if (error.response?.status === 401) {
                logout();
            }
        } finally {
            setIsLoadingAccounts(false);
        }
    };

    // Fetch beneficiaries for selected account with token
    const fetchBeneficiaries = async (accountId) => {
        try {
            setIsLoadingBeneficiaries(true);
            const response = await http.get(`/beneficiary?accountId=${accountId}`, apiConfig);

            if (response.data.status === true || response.data.status === 1) {
                const beneficiariesData = response.data.data || response.data.message?.data || [];
                setBeneficiaries(beneficiariesData);
            }
        } catch (error) {
            console.error('Error fetching beneficiaries:', error);
            setBeneficiaries([]);
            if (error.response?.status === 401) {
                logout();
            }
        } finally {
            setIsLoadingBeneficiaries(false);
        }
    };

    // Handle account selection
    const handleAccountSelect = (index, account) => {
        setSelectedAccountIndex(index);
        setSelectedAccountId(account.id);
        fetchBeneficiaries(account.id);
    };

    // Select beneficiary from ribbon
    const selectBeneficiary = (beneficiary) => {
        // Find bank for this beneficiary
        const bank = banks.find(b => b.id.toString() === beneficiary.bank_id?.toString());

        if (bank) {
            setSelectedBank({
                value: bank.bank_code,
                label: bank.bank_name
            });
        }

        setAccountNumber(beneficiary.accountNo);
        setAccountName(beneficiary.name);
        setAccountNameVerified(true);

        // Auto-verify if we have bank selected
        if (bank) {
            verifyAccountNumber(beneficiary.accountNo);
        }

        toast.success(`Selected ${beneficiary.name}`);
    };

    // Get bank data with token
    const getAllBanks = async () => {
        try {
            const response = await http.get('/get-banks', {
                params: { page: 1 },
                ...apiConfig
            });
            if (response.status === 200) {
                setBanks(response.data.data);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                logout();
            } else {
                toast.error('Something Went Wrong');
            }
        }
    };

    const handleSelectBank = (selectedOption) => {
        setSelectedBank(selectedOption);
        // Reset account verification when bank changes
        if (accountNumber.length === 10) {
            verifyAccountNumber(accountNumber);
        }
    };

    // Verify account number with token
    const verifyAccountNumber = async (number) => {
        setAccountNumber(number);
        if (!selectedBank) {
            toast.warning('Please select a bank first');
            return;
        }

        try {
            setIsVerifying(true);
            const response = await http.post('/verify-account-full', {
                bank_code: selectedBank.value,
                account_number: number,
            }, apiConfig);

            if (response.data.status === 1) {
                toast.info(response.data.message);
                setAccountNameVerified(true);
                setAccountName(response.data.data.account_name || response.data.data.first_name + ' ' + response.data.data.last_name);
            } else {
                toast.warning(response.data.message);
                setAccountNameVerified(false);
                setAccountName("");
            }
        } catch (error) {
            if (error.response?.status === 401) {
                logout();
            } else {
                toast.error('Account verification failed');
            }
            setAccountName("");
        } finally {
            setIsVerifying(false);
        }
    };

    // Initial useEffect
    useEffect(() => {
        getAllBanks();
        getUserAccounts();
    }, []);

    // Beneficiary Ribbon Component
    const BeneficiaryRibbon = () => {
        if (beneficiaries.length === 0) return null;

        const firstThree = beneficiaries.slice(0, 3);

        return (
            <div className="beneficiary-ribbon mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                        <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                        Frequent Beneficiaries
                    </h6>
                    {beneficiaries.length > 3 && (
                        <button
                            className="btn btn-link p-0 text-primary"
                            onClick={() => setShowBeneficiaryModal(true)}
                        >
                            See all ({beneficiaries.length})
                        </button>
                    )}
                </div>

                <div className="d-flex">
                    {firstThree.map((beneficiary, index) => (
                        <div
                            key={beneficiary.id || index}
                            className="beneficiary-item me-3 text-center"
                            onClick={() => selectBeneficiary(beneficiary)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="avatar-circle mb-2">
                                <div className="avatar-initial">
                                    {beneficiary.name?.charAt(0) || 'B'}
                                </div>
                            </div>
                            <div className="beneficiary-info">
                                <div className="beneficiary-name small">
                                    {beneficiary.name?.split(' ')[0] || beneficiary.name}
                                </div>
                                <div className="beneficiary-number text-muted x-small">
                                    {beneficiary.accountNo?.substring(beneficiary.accountNo.length - 4)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Accounts Ribbon Component
    const AccountsRibbon = () => {
        if (isLoadingAccounts) {
            return (
                <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading accounts...</span>
                </div>
            );
        }

        if (accounts.length === 0) {
            return (
                <div className="alert alert-warning">
                    No accounts found. Please add an account first.
                </div>
            );
        }

        return (
            <div className="accounts-ribbon mb-4">
                <h6 className="mb-2">Select Source Account</h6>
                <div className="d-flex overflow-auto pb-2" style={{ gap: '10px' }}>
                    {accounts.map((account, index) => (
                        <div
                            key={account.id}
                            className={`account-card ${selectedAccountIndex === index ? 'selected' : ''}`}
                            onClick={() => handleAccountSelect(index, account)}
                            style={{ cursor: 'pointer', minWidth: '220px' }}
                        >
                            <div className="p-3 rounded" style={{
                                backgroundColor: selectedAccountIndex === index ? '#351880' : '#f8f9fa',
                                border: `1px solid ${selectedAccountIndex === index ? '#351880' : '#dee2e6'}`
                            }}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="text-truncate" style={{
                                            color: selectedAccountIndex === index ? '#fff' : '#212529',
                                            fontWeight: '500',
                                            fontSize: '0.875rem'
                                        }}>
                                            {account.account_type?.savings_product || 'Account'}
                                        </div>
                                        <div className="mt-1" style={{
                                            color: selectedAccountIndex === index ? '#fff' : '#212529',
                                            fontWeight: 'bold',
                                            fontSize: '1rem'
                                        }}>
                                            ₦{account.user_wallet?.balance || '0.00'}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <div className="x-small" style={{
                                        color: selectedAccountIndex === index ? 'rgba(255,255,255,0.8)' : '#6c757d'
                                    }}>
                                        A/C: {account.virtual_accnum || 'N/A'}
                                    </div>
                                    <div className="x-small text-truncate" style={{
                                        color: selectedAccountIndex === index ? 'rgba(255,255,255,0.7)' : '#6c757d'
                                    }}>
                                        {account.bank?.bank_name || 'Bank'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Beneficiary Modal Component
    const BeneficiaryModal = () => (
        <Offcanvas
            show={showBeneficiaryModal}
            onHide={() => setShowBeneficiaryModal(false)}
            placement="end"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    All Beneficiaries ({beneficiaries.length})
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {isLoadingBeneficiaries ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p className="mt-3">Loading beneficiaries...</p>
                    </div>
                ) : beneficiaries.length === 0 ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faUserCircle} size="3x" className="text-muted mb-3" />
                        <p>No beneficiaries found</p>
                        <p className="text-muted small">Beneficiaries will appear here after successful transfers</p>
                    </div>
                ) : (
                    <div className="list-group">
                        {beneficiaries.map((beneficiary, index) => (
                            <div
                                key={beneficiary.id || index}
                                className="list-group-item list-group-item-action"
                                onClick={() => {
                                    selectBeneficiary(beneficiary);
                                    setShowBeneficiaryModal(false);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center">
                                    <div className="avatar-circle me-3">
                                        <div className="avatar-initial">
                                            {beneficiary.name?.charAt(0) || 'B'}
                                        </div>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="fw-medium">{beneficiary.name}</div>
                                        <div className="text-muted small">{beneficiary.accountNo}</div>
                                        {beneficiary.bank_name && (
                                            <div className="text-muted x-small">{beneficiary.bank_name}</div>
                                        )}
                                    </div>
                                    <FontAwesomeIcon icon={faUserCircle} className="text-muted" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );

    // CSS styles
    const beneficiaryStyles = `
        .beneficiary-item:hover .avatar-circle {
            transform: translateY(-2px);
            transition: transform 0.2s;
        }

        .avatar-circle {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto;
        }

        .avatar-initial {
            color: white;
            font-size: 20px;
            font-weight: bold;
        }

        .beneficiary-name {
            font-weight: 500;
            margin-top: 4px;
        }

        .beneficiary-number {
            font-size: 11px;
        }

        .account-card {
            transition: all 0.2s ease;
        }

        .account-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .account-card.selected {
            box-shadow: 0 4px 12px rgba(53, 24, 128, 0.2);
        }

        .x-small {
            font-size: 0.75rem;
        }

        .beneficiary-ribbon {
            background: #f8f9fa;
            padding: 1rem;
            border-radius: 8px;
            border: 1px solid #dee2e6;
        }

        .accounts-ribbon {
            background: white;
            padding: 1rem;
            border-radius: 8px;
            border: 1px solid #dee2e6;
        }
    `;

    const validateForm = () => {
        if (!accountNumber || !selectedBank || !description || !accountName || !amount) {
            toast.error('Please fill in all fields');
            return false;
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validateForm()) {
            setIsInitialising(true);
            try {
                const response = await http.post('/transactions/initiate-transaction', {
                    final_amount: amount,
                    transaction_type: 8,
                    destination_account: accountNumber,
                    receipient_name: accountName,
                    bank_code: selectedBank.value,
                    description: description,
                }, apiConfig).then((res) => {
                    settransactionRefrence(res.data.data.transaction_reference);
                    setIsInitialising(false);
                    setShowPasswordModal(true);
                });
            } catch (error) {
                setIsInitialising(false);
                Swal.fire({
                    title: "Failed",
                    text: 'Initialization Failed: ' + error?.response?.data?.message,
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        }
    };

    const handleContinue = async () => {
        if (validateForm()) {
            try {
                setIsBuying(true);
                const response = await http.post('/transactions/bank-transfer', {
                    final_amount: amount,
                    transaction_reference: transactionRefrence,
                    destination_account: accountNumber,
                    receipient_name: accountName,
                    bank_code: selectedBank.value,
                    transaction_pin: transactionPin,
                    description: description,
                    selectedAccountId:selectedAccountId
                }, apiConfig).then((trans) => {
                    if (trans.data.status === true) {
                        Swal.fire({
                            title: trans.data.message,
                            text: `You successfully transfer ${amount} to ${accountName} (${accountNumber}) \nReference Number : ${transactionRefrence}`,
                            icon: 'success',
                            confirmButtonText: 'OK',
                        });
                        setIsBuying(false);
                        setAmount('');
                        setAccountName('');
                        setSelectedBank('');
                        setAccountNumber('');
                        setDescription('');
                        setTransactionPin('');
                        setShowPasswordModal(false);

                        // Refresh beneficiaries after successful transfer
                        if (selectedAccountId) {
                            fetchBeneficiaries(selectedAccountId);
                        }
                    } else {
                        Swal.fire({
                            title: "Transfer Failed",
                            text: `Your transfer of ${amount} to ${accountName} (${accountNumber}) failed`,
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                        setIsBuying(false);
                    }
                }).catch((error) => {
                    setIsBuying(false);
                    toast.error('Transfer Failed, ' + error?.response?.data?.message);
                });
            } catch (error) {
                if (error.response?.status === 401) {
                    logout();
                }
                setIsBuying(false);
                toast.error('Transfer Failed, ' + error?.response?.data?.message);
            }
        }
    };

    return (
        <>
            <style>{beneficiaryStyles}</style>

            {isLoading ? (
                <PreLoader />
            ) : (
                <>
                    <Sidebar />
                    <Header user={user} />

                    <div className="content">
                        <form className="mb-9" onSubmit={handleSubmit}>
                            <div className="row g-5">
                                <div className="col-xl-8">
                                    <h4 className="fs-1 mb-4">Fund Transfer</h4>

                                    {/* Accounts Ribbon */}
                                    <AccountsRibbon />

                                    {/* Beneficiaries Ribbon */}
                                    <BeneficiaryRibbon />

                                    <div className="row gx-3 gy-4">
                                        <div className="col-sm-6 col-md-6">
                                            {isLoading ? (
                                                <Spinner animation="border" variant="primary" />
                                            ) : (
                                                <div className="form-floating">
                                                    <Select
                                                        id="floatingSelectTask"
                                                        value={selectedBank}
                                                        onChange={handleSelectBank}
                                                        options={banks.map((bank) => ({
                                                            value: bank.bank_code,
                                                            label: bank.bank_name,
                                                        }))}
                                                        required
                                                    />
                                                    <label htmlFor="floatingSelectTask">Select Bank</label>
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-sm-6 col-md-6">
                                            <div className="form-floating">
                                                <input
                                                    className="form-control"
                                                    id="floatingEventInput"
                                                    type="text"
                                                    placeholder="Account Number"
                                                    maxLength={10}
                                                    onChange={(e) => {
                                                        setAccountNumber(e.target.value);
                                                        if (e.target.value.length === 10 && selectedBank) {
                                                            verifyAccountNumber(e.target.value);
                                                        }
                                                    }}
                                                    value={accountNumber}
                                                    disabled={!selectedBank}
                                                    required
                                                />
                                                <label htmlFor="floatingEventInput">Account Number</label>
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-md-6">
                                            <div className="form-floating">
                                                <input
                                                    className="form-control"
                                                    id="floatingEventInput"
                                                    type="text"
                                                    readOnly={true}
                                                    placeholder="Account Name"
                                                    value={accountName}
                                                    disabled={!accountNameVerified}
                                                />
                                                {isVerifying && (
                                                    <button
                                                        type="button"
                                                        style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            right: '10px',
                                                            transform: 'translateY(-50%)',
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                        }}
                                                        className="btn btn-link password-toggle"
                                                    >
                                                        <Spinner animation="border" size="sm" />
                                                    </button>
                                                )}
                                                <label htmlFor="floatingEventInput">Account Name</label>
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-md-6">
                                            <div className="form-floating">
                                                <input
                                                    className="form-control"
                                                    id="floatingEventInput"
                                                    type="number"
                                                    step={2}
                                                    placeholder="Amount"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    required
                                                />
                                                <label htmlFor="floatingEventInput">Amount</label>
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-md-6">
                                            <div className="row">
                                                {defaultAmount.map((amt, index) => (
                                                    <div key={index} className="col-4">
                                                        <span
                                                            onClick={() => setAmount(amt)}
                                                            className={`btn btn-sm rounded-pill w-100 me-3 mb-2 fs--1 fs-sm-0 ${amount === amt ? 'btn-warning' : 'btn-outline-warning'}`}
                                                        >
                                                            {amt}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="col-sm-12 col-md-6 gy-6">
                                            <div className="form-floating">
                                                <textarea
                                                    className="form-control"
                                                    id="floatingProjectDescription"
                                                    placeholder="Leave a comment here"
                                                    style={{ height: '50px' }}
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    required
                                                ></textarea>
                                                <label htmlFor="floatingProjectDescription">Narration</label>
                                            </div>
                                        </div>

                                        <div className="col-12 col-sm-auto col-xl-4">
                                            <div className="d-flex">
                                                <Button variant="primary"
                                                    type="submit"
                                                    disabled={isInitialising}>
                                                    {isInitialising ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" /> Initialising...
                                                        </>
                                                    ) : (
                                                        "Continue"
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <TransactionHistory transaction_type={8} title="Transfer Histories" />
                        <Footer />
                    </div>

                    {/* Beneficiary Modal */}
                    <BeneficiaryModal />

                    {/* Existing Password Modal */}
                    <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} backdrop="static">
                        <Modal.Header closeButton>
                            <Modal.Title>Enter Transaction PIN</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="form-floating">
                                <input
                                    className="form-control"
                                    type={showPassword ? 'number' : 'password'}
                                    maxLength={4}
                                    placeholder="Transaction Pin"
                                    value={transactionPin}
                                    onChange={(e) => setTransactionPin(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    className="btn btn-link password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                                <label htmlFor="floatingEventInput">Transaction PIN</label>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary"
                                onClick={() => setShowPasswordModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary"
                                onClick={handleContinue} disabled={isBuying}>
                                {isBuying ? (
                                    <>
                                        <Spinner animation="border" size="sm" /> Loading...
                                    </>
                                ) : (
                                    "Transfer Now"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <ToastContainer />
                </>
            )}
        </>
    );
}

export default Transfer;